import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { CircularProgress } from "@mui/material";
import { CustomersPageHeader } from "./customers-page-header";
import { CustomersToolbar } from "./customers-toolbar";
import { CustomersTable } from "./customers-table";
import { CustomersPagination } from "./customers-pagination";
import CustomerDetailDrawer from "./customer-detail-drawer";
import {
  filterCustomers,
  mapSellerCustomerToRow,
  totalPagesFor,
} from "./customers-utils";
import { GET_SELLER_CUSTOMERS } from "../../../../Utils/Queries";

const PAGE_SIZE = 10;

export default function SellerCustomersPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(1);
  const [detailCustomer, setDetailCustomer] = useState(null);

  const { data, loading, error } = useQuery(GET_SELLER_CUSTOMERS, {
    variables: { page, limit: PAGE_SIZE },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const result = data?.getSellerCustomers;
  const docs = useMemo(() => result?.docs ?? [], [result?.docs]);

  const rows = useMemo(
    () => docs.map(mapSellerCustomerToRow).filter(Boolean),
    [docs],
  );

  const filtered = useMemo(() => filterCustomers(rows, search), [rows, search]);

  const totalPagesFromApi =
    typeof result?.totalPages === "number" && result.totalPages > 0
      ? result.totalPages
      : null;
  const totalPages = totalPagesFromApi ?? totalPagesFor(rows.length, PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const safePage = Math.min(page, totalPages);
  const isLoading = loading && !data;

  return (
    <div className="pb-12 pt-10 md:pt-6">
      <CustomersPageHeader />

      <div className="min-w-0 rounded-[14px] border border-[#EFEFEF] bg-white p-4 shadow-[0_1px_3px_rgba(15,23,42,0.04)] sm:p-5 md:p-6 lg:p-8">
        <CustomersToolbar
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
        />

        <div className="md:overflow-x-auto">
          {isLoading ? (
            <div className="flex min-h-[240px] items-center justify-center">
              <CircularProgress size={28} sx={{ color: "#2E1834" }} />
            </div>
          ) : error ? (
            <div className="flex min-h-[240px] items-center justify-center px-4">
              <p className="my-0 text-center text-[13px] font-medium text-[#9CA3AF]">
                We couldn’t load customers right now. Please try again later.
              </p>
            </div>
          ) : (
            <CustomersTable
              rows={filtered}
              onViewDetails={(c) => setDetailCustomer(c)}
            />
          )}
        </div>

        <CustomersPagination
          page={safePage}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      </div>

      <CustomerDetailDrawer
        open={detailCustomer != null}
        customer={detailCustomer}
        onClose={() => setDetailCustomer(null)}
      />
    </div>
  );
}
